import React, { useState, useEffect, useRef, useMemo } from 'react';
import UnifiedCard from '../Card/UnifiedCard';
import CardInfoOverlay from '../Card/CardInfoOverlay';
import './SearchFilterBar.css';

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

const RARITY_ORDER = { common: 0, uncommon: 1, rare: 2, mythic: 3, special: 4 };

// Helper function to compute sortPrice from card's embedded price data
// Used when cards don't have sortPrice pre-computed (e.g., external/binder cards)
// Exported: the Binders dashboard prices entries with the same math.
export function computeSortPrice(card) {
    if (!card) return null;

    // If sortPrice already exists, use it
    if (card.sortPrice !== undefined && card.sortPrice !== null) {
        return card.sortPrice;
    }

    // Helper to parse JSON array if needed
    const parseArray = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                return JSON.parse(val);
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    // Try min_prices first (pre-calculated lowest prices per finish)
    if (card.min_prices) {
        const minPrices = parseArray(card.min_prices);
        let lowest = null;
        for (const price of minPrices) {
            if (price !== null && price !== undefined) {
                const numPrice = parseFloat(price);
                if (!isNaN(numPrice) && numPrice > 0) {
                    if (lowest === null || numPrice < lowest) {
                        lowest = numPrice;
                    }
                }
            }
        }
        if (lowest !== null) return lowest;
    }

    // Fallback: check tcgplayer_prices and cardkingdom_prices
    const tcgPrices = parseArray(card.tcgplayer_prices);
    const ckPrices = parseArray(card.cardkingdom_prices);
    const allPrices = [...tcgPrices, ...ckPrices];

    let lowest = null;
    for (const price of allPrices) {
        const numPrice = parseFloat(price);
        if (!isNaN(numPrice) && numPrice > 0) {
            if (lowest === null || numPrice < lowest) {
                lowest = numPrice;
            }
        }
    }

    return lowest;
}

const DEFAULT_SORT_OPTIONS = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'release-asc', label: 'Release Date (Oldest)' },
    { value: 'release-desc', label: 'Release Date (Newest)' },
    { value: 'rarity-asc', label: 'Rarity (Common → Mythic)' },
    { value: 'rarity-desc', label: 'Rarity (Mythic → Common)' },
    { value: 'price-asc', label: 'Price (Low → High)' },
    { value: 'price-desc', label: 'Price (High → Low)' },
    { value: 'quantity-desc', label: 'Quantity (High → Low)' },
    { value: 'quantity-asc', label: 'Quantity (Low → High)' }
];

/**
 * SearchFilterBar - Master Search/Filter Widget
 * 
 * Supports two modes:
 * 1. Widget mode (showCardDisplay=true): Self-contained with card display
 * 2. Filter-only mode (showCardDisplay=false): Parent handles card display
 */
function SearchFilterBar({
    // === BACKWARD COMPATIBLE PROPS (for Trade.js, Binders.js, etc.) ===
    availableSets: externalSets = null,  // External sets array
    setsLoading: externalSetsLoading = false,
    onFiltersChange,           // Callback when filters change
    onDisplayOptionsChange,    // Callback when display options change
    onSearch,                  // Callback when search button clicked
    loading: externalLoading,  // External loading state
    extraLeftContent = null,   // Extra content in filter bar
    availableFinishes: externalFinishes = null, // External finishes for filter mode

    // === UI TOGGLES ===
    showSetFilter = true,
    showRarityFilter = true,
    showNameFilter = true,
    showDisplayOptions = true,
    showSearchButton = true,
    showCardNameAutocomplete = true,
    showFinishFilter = true,
    showCardDisplay = false,   // DEFAULT FALSE for backward compatibility!
    namePlaceholder = "Card name...",
    cardMode = "collection",

    // === INITIAL VALUES ===
    initialFilters = {},
    initialDisplayOptions = {},
    autoSearchOnMount = false, // Run the search immediately if initialFilters has content (e.g. arriving from the homepage hero search)
    sortOptions = DEFAULT_SORT_OPTIONS,
    filterSetsToShow = null,

    // === DUAL-MODE TOGGLE ===
    dualMode = false,
    toggleOn = true,
    onToggleChange = null,

    // === WIDGET MODE PROPS ===
    mode = "search",           // "search" or "filter"
    externalCards = [],        // Cards for filter mode
    onCardClick,
    onProcessedCards,
    requireFilters = true,
    emptyTitle = "Browse All Cards",
    emptyMessage = "Search for any card by name, set, or rarity.",

    // === CARD INTERACTION PROPS (for MakeRequest, Trade, etc.) ===
    onAddToTrade,              // Handler for adding collection cards to trade
    onAddToTradeWithFinish,    // Handler for adding search cards to trade (with finish selection)
    onAddToCollection,         // Handler for adding cards to collection (have/want with finish selection)
    activeTab = null,          // 'have' or 'want' - passed to UnifiedCard for trade context
    matchedCardKeys = null,    // Set of "uuid-setcode" keys for cards that should be highlighted (for trade matching)
    matchedCardNames = null,   // Set of lowercase card names for "any" finish matching
    getAddState = null,        // (card, entry) => { disabled } - grey out the add button (quantity caps)

    // === BINDER INTERACTION PROPS ===
    onUpdateEntry,             // Handler for updating binder entry quantity
    onDeleteEntry,             // Handler for deleting binder entry
    entryType = null           // 'have' or 'want' for binder mode
}) {
    // ========== STATE ==========
    const [cards, setCards] = useState([]);
    const [internalSets, setInternalSets] = useState([]);
    const [internalSetsLoading, setInternalSetsLoading] = useState(true);
    const [internalLoading, setInternalLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(showCardDisplay ? (mode === "filter") : true);
    const [currentPage, setCurrentPage] = useState(1);

    const [filters, setFilters] = useState({
        setCodes: initialFilters.setCodes || [],
        name: initialFilters.name || '',
        rarities: initialFilters.rarities || [],
        finishes: initialFilters.finishes || []
    });

    // Load display options from localStorage with fallbacks to initialDisplayOptions
    const [displayOptions, setDisplayOptions] = useState(() => {
        try {
            const saved = localStorage.getItem('searchFilterBarDisplayOptions');
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    cardsPerPage: parsed.cardsPerPage || initialDisplayOptions.cardsPerPage || 12,
                    displayMode: parsed.displayMode || initialDisplayOptions.displayMode || 'standard',
                    sortBy: initialDisplayOptions.sortBy || 'name-asc', // Don't persist sortBy - use initial or default
                };
            }
        } catch { }
        return {
            cardsPerPage: initialDisplayOptions.cardsPerPage || 12,
            displayMode: initialDisplayOptions.displayMode || 'standard',
            sortBy: initialDisplayOptions.sortBy || 'name-asc',
        };
    });

    const [setSearchTerm, setSetSearchTerm] = useState('');
    const [isSetDropdownOpen, setIsSetDropdownOpen] = useState(false);
    const [selectedSetIndex, setSelectedSetIndex] = useState(-1);
    const [cardNames, setCardNames] = useState([]);
    const [isCardNameDropdownOpen, setIsCardNameDropdownOpen] = useState(false);
    const [cardNamesLoading, setCardNamesLoading] = useState(false);
    const [selectedCardNameIndex, setSelectedCardNameIndex] = useState(-1);

    // Card Info Overlay state (for navigation between cards)
    const [selectedOverlayIndex, setSelectedOverlayIndex] = useState(null);
    const [overlayImageError, setOverlayImageError] = useState(false);

    const setDropdownRef = useRef(null);
    const cardNameDropdownRef = useRef(null);

    // ========== COMPUTED VALUES ==========
    const availableSets = externalSets || internalSets;
    const setsLoading = externalSets ? externalSetsLoading : internalSetsLoading;
    const loading = externalLoading !== undefined ? externalLoading : internalLoading;

    const effectiveShowSearchButton = dualMode ? !toggleOn : showSearchButton;
    const effectiveShowCardNameAutocomplete = dualMode ? !toggleOn : showCardNameAutocomplete;
    const effectiveFilterSetsToShow = dualMode && toggleOn ? filterSetsToShow : (dualMode ? null : filterSetsToShow);

    const rawCards = mode === "filter" ? externalCards : cards;

    // Extract finishes from cards - use external if provided, otherwise compute from cards
    // For collection cards ({card, binder} structure), extract from binder.finish
    // For search result cards, extract from card.finishes
    const availableFinishes = useMemo(() => {
        if (externalFinishes && externalFinishes.length > 0) return externalFinishes;
        if (!rawCards.length) return [];
        const finishSet = new Set();
        rawCards.forEach(item => {
            // Collection card structure: {card, binder}
            const binder = item.binder || item.entry;
            if (binder?.finish) {
                finishSet.add(binder.finish.toLowerCase());
            } else {
                // Search result card structure: just card object
                const card = item.card || item;
                if (card?.finishes) {
                    (Array.isArray(card.finishes) ? card.finishes : card.finishes.split(',').map(f => f.trim()))
                        .forEach(f => finishSet.add(f.toLowerCase()));
                }
            }
        });
        return Array.from(finishSet).sort();
    }, [rawCards, externalFinishes]);

    // Filter cards by name, set, rarity, and finish (for widget mode)
    const filteredCards = useMemo(() => {
        if (!showCardDisplay) return rawCards;

        return rawCards.filter(item => {
            // Support for collection cards that have {card, binder} structure
            const card = item.card || item;
            const binder = item.binder || item.entry;
            if (!card) return false;

            // Name filter
            if (filters.name && !card.name?.toLowerCase().includes(filters.name.toLowerCase())) return false;

            // Set filter
            if (filters.setCodes.length > 0 && !filters.setCodes.includes(card.setCode)) return false;

            // Rarity filter
            if (filters.rarities.length > 0 && !filters.rarities.includes(card.rarity?.toLowerCase())) return false;

            // Finish filter - check binder.finish for collection cards, card.finishes for search results
            if (filters.finishes.length > 0) {
                if (binder?.finish) {
                    // Collection card: check binder.finish
                    if (!filters.finishes.some(f => f.toLowerCase() === binder.finish.toLowerCase())) return false;
                } else if (card.finishes) {
                    // Search result card: check card.finishes array
                    const cardFinishes = (Array.isArray(card.finishes) ? card.finishes : card.finishes.split(',').map(f => f.trim()))
                        .map(f => f.toLowerCase());
                    if (!filters.finishes.some(f => cardFinishes.includes(f.toLowerCase()))) return false;
                } else {
                    return false;
                }
            }

            return true;
        });
    }, [rawCards, filters, showCardDisplay]);

    // Sort cards (for widget mode) - supports {card, binder} structure
    const sortedCards = useMemo(() => {
        if (!showCardDisplay) return filteredCards;
        let sorted = [...filteredCards];

        // Helper to get card from item (supports {card, binder} structure)
        const getCard = (item) => item.card || item;
        const getBinder = (item) => item.binder || item.entry || null;

        // Helper to check if a card is matched (by key or by name for 'any' finish)
        const isMatched = (item) => {
            const card = getCard(item);
            const binder = getBinder(item);
            
            // Check by uuid-setcode key
            if (matchedCardKeys && matchedCardKeys.size > 0) {
                const key = `${card.uuid}-${card.setCode}`;
                if (matchedCardKeys.has(key)) return true;
            }
            
            // Check by card name (for 'any' finish matching)
            if (matchedCardNames && matchedCardNames.size > 0 && card.name) {
                const cardNameLower = card.name.trim().toLowerCase();
                // If the binder entry has 'any' finish OR if we're matching by name
                if (matchedCardNames.has(cardNameLower)) {
                    // Also check if finish is 'any' on either side
                    if (binder?.finish === 'any' || matchedCardNames.has(cardNameLower)) {
                        return true;
                    }
                }
            }
            
            return false;
        };

        switch (displayOptions.sortBy) {
            case 'name-asc':
                sorted.sort((a, b) => (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'name-desc':
                sorted.sort((a, b) => (getCard(b).name || '').localeCompare(getCard(a).name || ''));
                break;
            case 'release-asc':
                sorted.sort((a, b) => {
                    const setA = availableSets.find(s => s.setCode === getCard(a).setCode);
                    const setB = availableSets.find(s => s.setCode === getCard(b).setCode);
                    return (setA?.release_date || '').localeCompare(setB?.release_date || '');
                });
                break;
            case 'release-desc':
                sorted.sort((a, b) => {
                    const setA = availableSets.find(s => s.setCode === getCard(a).setCode);
                    const setB = availableSets.find(s => s.setCode === getCard(b).setCode);
                    return (setB?.release_date || '').localeCompare(setA?.release_date || '');
                });
                break;
            case 'rarity-asc':
                sorted.sort((a, b) => (RARITY_ORDER[getCard(a).rarity?.toLowerCase()] ?? 99) - (RARITY_ORDER[getCard(b).rarity?.toLowerCase()] ?? 99) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'rarity-desc':
                sorted.sort((a, b) => (RARITY_ORDER[getCard(b).rarity?.toLowerCase()] ?? 99) - (RARITY_ORDER[getCard(a).rarity?.toLowerCase()] ?? 99) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'price-asc':
                sorted.sort((a, b) => (computeSortPrice(getCard(a)) ?? Infinity) - (computeSortPrice(getCard(b)) ?? Infinity) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'price-desc':
                sorted.sort((a, b) => (computeSortPrice(getCard(b)) ?? 0) - (computeSortPrice(getCard(a)) ?? 0) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'quantity-desc':
                sorted.sort((a, b) => (getBinder(b)?.quantity || 0) - (getBinder(a)?.quantity || 0) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            case 'quantity-asc':
                sorted.sort((a, b) => (getBinder(a)?.quantity || 0) - (getBinder(b)?.quantity || 0) || (getCard(a).name || '').localeCompare(getCard(b).name || ''));
                break;
            default: break;
        }

        // If we have matched cards, put them first
        if (matchedCardKeys && matchedCardKeys.size > 0) {
            const matched = sorted.filter(isMatched);
            const unmatched = sorted.filter(item => !isMatched(item));
            sorted = [...matched, ...unmatched];
        }

        return sorted;
    }, [filteredCards, displayOptions.sortBy, availableSets, showCardDisplay, matchedCardKeys, matchedCardNames]);

    const totalPages = Math.ceil(sortedCards.length / displayOptions.cardsPerPage) || 1;
    const paginatedCards = useMemo(() => {
        const start = (currentPage - 1) * displayOptions.cardsPerPage;
        return sortedCards.slice(start, start + displayOptions.cardsPerPage);
    }, [sortedCards, currentPage, displayOptions.cardsPerPage]);

    // Notify parent of processed cards (widget mode)
    useEffect(() => {
        if (showCardDisplay && onProcessedCards) {
            onProcessedCards(sortedCards, { totalCards: rawCards.length, filteredCount: sortedCards.length, hasFinishFilter: filters.finishes.length > 0 });
        }
    }, [sortedCards, rawCards.length, filters.finishes.length, onProcessedCards, showCardDisplay]);

    // ========== RESET FILTERS when mode or toggle changes ==========
    useEffect(() => {
        // Reset filters when switching between My Cards/All Cards or Have/Want tabs
        setFilters({
            setCodes: initialFilters.setCodes || [],
            name: '',
            rarities: initialFilters.rarities || [],
            finishes: []
        });
        setCurrentPage(1);
        setHasSearched(mode === "filter"); // filter mode shows cards immediately
    }, [toggleOn, activeTab, mode]);

    // ========== FETCH SETS (only if not provided externally) ==========
    useEffect(() => {
        if (externalSets) return;
        (async () => {
            try {
                setInternalSetsLoading(true);
                const res = await fetch('/api/sets');
                if (!res.ok) throw new Error();
                const sets = await res.json();
                setInternalSets(sets.sort((a, b) => (a.setName || a.setCode).localeCompare(b.setName || b.setCode)));
            } catch { setInternalSets([]); }
            finally { setInternalSetsLoading(false); }
        })();
    }, [externalSets]);

    // ========== CLICK OUTSIDE DROPDOWN ==========
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (setDropdownRef.current && !setDropdownRef.current.contains(e.target)) setIsSetDropdownOpen(false);
            if (cardNameDropdownRef.current && !cardNameDropdownRef.current.contains(e.target)) setIsCardNameDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ========== SEARCH ==========
    const doSearch = async (nameOverride) => {
        // If onSearch callback provided, call it and let parent handle
        if (onSearch) {
            onSearch(filters, displayOptions);
            return;
        }

        // Otherwise, do internal search (widget mode)
        if (!showCardDisplay || mode !== "search") return;

        const searchName = (typeof nameOverride === 'string' ? nameOverride : filters.name).trim();
        const hasFilters = filters.setCodes.length > 0 || searchName || filters.rarities.length > 0;
        if (requireFilters && !hasFilters) {
            setError('Please enter a card name, select a set, or choose a rarity.');
            setHasSearched(true);
            return;
        }

        setInternalLoading(true);
        setError(null);
        setCurrentPage(1);
        setHasSearched(true);
        setFilters(f => ({ ...f, finishes: [] }));

        try {
            let searchNames = [];

            // If user typed a partial name (not from dropdown), first find matching card names
            // This is MUCH faster than searching all cards with LIKE
            if (searchName && searchName.length >= 3) {
                try {
                    const namesRes = await fetch(`/api/card-names/search?q=${encodeURIComponent(searchName)}`);
                    if (namesRes.ok) {
                        searchNames = await namesRes.json();
                        console.log('[SEARCH DEBUG] Found', searchNames.length, 'matching card names:', searchNames.slice(0, 5));
                        // Limit to top 50 matching names for performance
                        searchNames = searchNames.slice(0, 50);
                    }
                } catch (e) {
                    // If name lookup fails, fall back to regular search
                    console.warn('[SEARCH DEBUG] Card name lookup failed:', e);
                }
            }

            const params = new URLSearchParams();
            if (filters.setCodes.length) params.append('setCodes', filters.setCodes.join(','));

            // If we found matching card names, search by exact names instead of partial match
            if (searchNames.length > 0) {
                // Use || as delimiter since card names can contain commas (e.g., "Krenko, Mob Boss")
                params.append('names', searchNames.join('||'));
            } else if (searchName) {
                // Fallback to original partial name search
                params.append('name', searchName);
            }

            if (filters.rarities.length) params.append('rarities', filters.rarities.join(','));

            console.log('[SEARCH DEBUG] Final search URL:', `/api/cards/search?${params}`);
            const res = await fetch(`/api/cards/search?${params}`);
            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Search failed');
                setCards([]);
                return;
            }
            setCards(data.cards || []);
        } catch (err) {
            setError(err.message || 'Search failed');
            setCards([]);
        } finally {
            setInternalLoading(false);
        }
    };

    // Switch a dual-mode widget from "my cards" to "all cards" while
    // carrying the typed name across the remount (parents key on toggleOn).
    // The query rides BOTH channels: as onToggleChange's 2nd arg (parents
    // seed initialFilters + autoSearchOnMount deterministically) and in
    // sessionStorage as a fallback.
    const switchToAllCards = () => {
        const q = filters.name.trim() || null;
        try {
            if (q) sessionStorage.setItem('sfb_pending_search', q);
        } catch { }
        if (onToggleChange) onToggleChange(false, q);
    };

    // Auto-run the search once on mount when seeded from elsewhere:
    // - a pending query handed over by switchToAllCards()
    // - the homepage hero search (autoSearchOnMount + initialFilters)
    useEffect(() => {
        if (showCardDisplay && mode === 'search') {
            let pending = null;
            try {
                pending = sessionStorage.getItem('sfb_pending_search');
                if (pending) sessionStorage.removeItem('sfb_pending_search');
            } catch { }
            if (pending) {
                setFilters(f => ({ ...f, name: pending }));
                doSearch(pending);
                return;
            }
        }
        if (autoSearchOnMount && (initialFilters.name || '').trim()) {
            doSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ========== CARD NAME AUTOCOMPLETE ==========
    const fetchCardNamesRaw = async (term) => {
        if (term.length < 3) { setCardNames([]); return; }
        try {
            setCardNamesLoading(true);
            const res = await fetch(`/api/card-names/search?q=${encodeURIComponent(term)}`);
            if (!res.ok) throw new Error();
            setCardNames((await res.json()).slice(0, 20));
        } catch { setCardNames([]); }
        finally { setCardNamesLoading(false); }
    };
    const fetchCardNames = useMemo(() => debounce(fetchCardNamesRaw, 300), []);

    // ========== FILTER HANDLERS ==========
    const updateFilters = (newFilters) => {
        setFilters(newFilters);
        if (onFiltersChange) onFiltersChange(newFilters);
    };

    const updateDisplayOptions = (newOptions) => {
        setDisplayOptions(newOptions);
        if (onDisplayOptionsChange) onDisplayOptionsChange(newOptions);
        // Save cardsPerPage and displayMode to localStorage (not sortBy)
        try {
            localStorage.setItem('searchFilterBarDisplayOptions', JSON.stringify({
                cardsPerPage: newOptions.cardsPerPage,
                displayMode: newOptions.displayMode
            }));
        } catch { }
    };

    const updateFilter = (key, value) => updateFilters({ ...filters, [key]: value });
    const addSetCode = (code) => { if (code && !filters.setCodes.includes(code)) updateFilters({ ...filters, setCodes: [...filters.setCodes, code] }); };
    const removeSetCode = (code) => updateFilters({ ...filters, setCodes: filters.setCodes.filter(c => c !== code) });
    const addRarity = (r) => { if (r && !filters.rarities.includes(r)) updateFilters({ ...filters, rarities: [...filters.rarities, r] }); };
    const removeRarity = (r) => updateFilters({ ...filters, rarities: filters.rarities.filter(x => x !== r) });
    const addFinish = (f) => { if (f && !filters.finishes.includes(f)) updateFilters({ ...filters, finishes: [...filters.finishes, f] }); };
    const removeFinish = (f) => updateFilters({ ...filters, finishes: filters.finishes.filter(x => x !== f) });

    const formatFinish = (f) => f === 'nonfoil' ? 'Nonfoil' : f === 'foil' ? 'Foil' : f === 'etched' ? 'Etched' : f.charAt(0).toUpperCase() + f.slice(1);
    const getSetName = (code) => availableSets.find(s => s.setCode === code)?.setName || code;

    const filteredSets = useMemo(() => {
        return availableSets.filter(set => {
            const searchLower = setSearchTerm.toLowerCase();
            return ((set.setName || '').toLowerCase().includes(searchLower) || set.setCode.toLowerCase().includes(searchLower))
                && !filters.setCodes.includes(set.setCode)
                && (!effectiveFilterSetsToShow || effectiveFilterSetsToShow.includes(set.setCode));
        });
    }, [availableSets, setSearchTerm, filters.setCodes, effectiveFilterSetsToShow]);

    // ========== KEYBOARD HANDLERS ==========
    const handleSubmit = (e) => { e.preventDefault(); doSearch(); };
    const handleCardNameKeyDown = (e) => {
        if (!isCardNameDropdownOpen || !cardNames.length) {
            if (e.key === 'Enter') { e.preventDefault(); doSearch(); }
            return;
        }
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedCardNameIndex(i => i < cardNames.length - 1 ? i + 1 : 0); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedCardNameIndex(i => i > 0 ? i - 1 : cardNames.length - 1); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedCardNameIndex >= 0) { updateFilter('name', cardNames[selectedCardNameIndex]); setIsCardNameDropdownOpen(false); }
            else doSearch();
        }
        else if (e.key === 'Escape') setIsCardNameDropdownOpen(false);
    };
    const handleSetKeyDown = (e) => {
        if (!isSetDropdownOpen || !filteredSets.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedSetIndex(i => i < Math.min(filteredSets.length, 50) - 1 ? i + 1 : 0); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedSetIndex(i => i > 0 ? i - 1 : Math.min(filteredSets.length, 50) - 1); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedSetIndex >= 0) { addSetCode(filteredSets[selectedSetIndex].setCode); setSetSearchTerm(''); setIsSetDropdownOpen(false); }
        }
        else if (e.key === 'Escape') setIsSetDropdownOpen(false);
    };

    // ========== CARD INFO OVERLAY HANDLERS ==========
    const handleOpenOverlay = (idx) => {
        setSelectedOverlayIndex(idx);
        setOverlayImageError(false);
    };

    const handleCloseOverlay = () => {
        setSelectedOverlayIndex(null);
        setOverlayImageError(false);
    };

    const handlePreviousCard = () => {
        if (selectedOverlayIndex !== null && selectedOverlayIndex > 0) {
            setSelectedOverlayIndex(selectedOverlayIndex - 1);
            setOverlayImageError(false);
        }
    };

    const handleNextCard = () => {
        if (selectedOverlayIndex !== null && selectedOverlayIndex < paginatedCards.length - 1) {
            setSelectedOverlayIndex(selectedOverlayIndex + 1);
            setOverlayImageError(false);
        }
    };

    // Get overlay card data
    const overlayCardData = selectedOverlayIndex !== null && paginatedCards[selectedOverlayIndex]
        ? paginatedCards[selectedOverlayIndex]
        : null;
    const overlayCard = overlayCardData ? (overlayCardData.card || overlayCardData) : null;
    const overlayEntry = overlayCardData ? (overlayCardData.binder || overlayCardData.entry || null) : null;

    // Process embedded prices from card data for overlay display
    const overlayPrices = useMemo(() => {
        if (!overlayCard) return {};

        const pricesByFinish = {};

        // Parse finishes array
        let finishes = [];
        if (Array.isArray(overlayCard.finishes)) {
            finishes = overlayCard.finishes;
        } else if (typeof overlayCard.finishes === 'string') {
            try {
                finishes = JSON.parse(overlayCard.finishes);
            } catch (e) {
                finishes = [];
            }
        }

        if (finishes.length === 0) return {};

        // Helper to parse JSON array if needed
        const parseArray = (val) => {
            if (Array.isArray(val)) return val;
            if (typeof val === 'string') {
                try {
                    return JSON.parse(val);
                } catch (e) {
                    return [];
                }
            }
            return [];
        };

        const tcgplayerPrices = parseArray(overlayCard.tcgplayer_prices);
        const cardkingdomPrices = parseArray(overlayCard.cardkingdom_prices);
        const minPrices = parseArray(overlayCard.min_prices);

        // Map prices to finishes
        finishes.forEach((finish, index) => {
            const tcgPrice = tcgplayerPrices[index] !== undefined ? parseFloat(tcgplayerPrices[index]) : null;
            const ckPrice = cardkingdomPrices[index] !== undefined ? parseFloat(cardkingdomPrices[index]) : null;
            const minPrice = minPrices[index] !== undefined ? parseFloat(minPrices[index]) : null;

            // Validate prices
            const validTcg = tcgPrice !== null && !isNaN(tcgPrice) && tcgPrice > 0 ? tcgPrice : null;
            const validCk = ckPrice !== null && !isNaN(ckPrice) && ckPrice > 0 ? ckPrice : null;
            const validMin = minPrice !== null && !isNaN(minPrice) && minPrice > 0 ? minPrice : null;

            // Find best deal (lowest price)
            let usdPrice = validMin;
            let bestSource = null;

            if (!usdPrice) {
                const sources = [
                    { price: validTcg, source: 'tcgplayer' },
                    { price: validCk, source: 'cardkingdom' }
                ];
                for (const { price, source } of sources) {
                    if (price !== null && (usdPrice === null || price < usdPrice)) {
                        usdPrice = price;
                        bestSource = source;
                    }
                }
            } else {
                // Determine best source based on which matches min_price
                if (validTcg === validMin) bestSource = 'tcgplayer';
                else if (validCk === validMin) bestSource = 'cardkingdom';
            }

            pricesByFinish[finish] = {
                setCode: overlayCard.setCode,
                tcgplayer_price: validTcg !== null ? validTcg.toFixed(2) : null,
                cardkingdom_price: validCk !== null ? validCk.toFixed(2) : null,
                usd_price: usdPrice !== null ? usdPrice.toFixed(2) : null,
                best_source: bestSource
            };
        });

        return pricesByFinish;
    }, [overlayCard]);

    // Close overlay when sort/filter/page changes to avoid stale index issues
    useEffect(() => {
        setSelectedOverlayIndex(null);
        setOverlayImageError(false);
    }, [displayOptions.sortBy, filters, currentPage]);

    // ========== PAGINATION (widget mode) ==========
    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        const pages = []; for (let i = start; i <= end; i++) pages.push(i);
        return (
            <div className="pagination">
                <button className="pagination-button" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                <button className="pagination-button" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Prev</button>
                {pages.map(p => <button key={p} className={`pagination-button ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>)}
                <button className="pagination-button" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>Next</button>
                <button className="pagination-button" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                <span className="pagination-info">Page {currentPage} of {totalPages}</span>
            </div>
        );
    };

    // ========== RENDER ==========
    return (
        <>
            {/* Scope bar: labeled state instead of the old mode toggle.
                Same toggleOn plumbing - parents are unchanged. */}
            {dualMode && onToggleChange && (
                <div className="scope-bar">
                    {toggleOn ? (
                        <>
                            <span className="scope-label">
                                Showing <strong>{entryType === 'want' ? 'your wishlist' : 'your cards'}</strong>
                            </span>
                            <button type="button" className="scope-switch-btn" onClick={switchToAllCards}>
                                Search all cards &rarr;
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="scope-label">
                                Showing <strong>all cards</strong>
                            </span>
                            <button type="button" className="scope-switch-btn scope-back" onClick={() => onToggleChange(true)}>
                                &larr; Back to your cards
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Filter Bar */}
            <div className={`search-filters-bar${(filters.setCodes.length > 0 || filters.rarities.length > 0 || filters.finishes.length > 0) ? ' has-selected-bar' : ''}`}>
                <form className="filters-bar-left" onSubmit={handleSubmit}>
                    <div className="filter-group-inline">
                        {showNameFilter && (
                            <div className="searchable-dropdown" ref={cardNameDropdownRef}>
                                <input type="text" className="filter-input" placeholder={namePlaceholder} value={filters.name}
                                    onChange={(e) => { updateFilter('name', e.target.value); if (effectiveShowCardNameAutocomplete && e.target.value.length >= 3) { fetchCardNames(e.target.value); setIsCardNameDropdownOpen(true); } else { setIsCardNameDropdownOpen(false); setCardNames([]); } }}
                                    onFocus={() => { if (effectiveShowCardNameAutocomplete && filters.name.length >= 3) setIsCardNameDropdownOpen(true); }}
                                    onKeyDown={handleCardNameKeyDown} />
                                {effectiveShowCardNameAutocomplete && isCardNameDropdownOpen && (
                                    <div className="searchable-dropdown-menu">
                                        {cardNamesLoading ? <div className="dropdown-loading">Searching...</div> :
                                            cardNames.length > 0 ? cardNames.map((n, i) => <div key={n} className={`dropdown-option ${i === selectedCardNameIndex ? 'dropdown-option-selected' : ''}`} onClick={() => {
                                                // Update filter with selected name
                                                setFilters(f => ({ ...f, name: n }));
                                                setIsCardNameDropdownOpen(false);
                                                // Auto-search after selection in widget mode
                                                if (showCardDisplay && mode === "search") {
                                                    setTimeout(() => doSearch(), 100);
                                                } else if (onFiltersChange) {
                                                    onFiltersChange({ ...filters, name: n });
                                                }
                                            }}>{n}</div>) :
                                                <div className="dropdown-no-results">{filters.name.length < 3 ? 'Type 3+ chars...' : 'No cards found'}</div>}
                                    </div>
                                )}
                            </div>
                        )}
                        {showSetFilter && (
                            <div className="searchable-dropdown" ref={setDropdownRef}>
                                <input type="text" className="filter-input" placeholder="Sets..." value={setSearchTerm}
                                    onChange={(e) => { setSetSearchTerm(e.target.value); setSelectedSetIndex(-1); if (!isSetDropdownOpen) setIsSetDropdownOpen(true); }}
                                    onFocus={() => { setIsSetDropdownOpen(true); setSelectedSetIndex(-1); }} onKeyDown={handleSetKeyDown} disabled={setsLoading} />
                                {isSetDropdownOpen && (
                                    <div className="searchable-dropdown-menu">
                                        {setsLoading ? <div className="dropdown-loading">Loading...</div> :
                                            filteredSets.length > 0 ? filteredSets.slice(0, 50).map((s, i) => <div key={s.setCode} className={`dropdown-option ${i === selectedSetIndex ? 'dropdown-option-selected' : ''}`} onClick={() => { addSetCode(s.setCode); setSetSearchTerm(''); setIsSetDropdownOpen(false); }}>{s.setName || s.setCode}</div>) :
                                                <div className="dropdown-no-results">{setSearchTerm ? 'No sets found' : 'Type to search...'}</div>}
                                    </div>
                                )}
                            </div>
                        )}
                        {showRarityFilter && (
                            <select className="filter-select" value="" onChange={(e) => { if (e.target.value) addRarity(e.target.value); }}>
                                <option value="">Rarity...</option>
                                {['common', 'uncommon', 'rare', 'mythic', 'special'].filter(r => !filters.rarities.includes(r)).map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                            </select>
                        )}
                        {showFinishFilter && availableFinishes.length > 0 && (
                            <select className="filter-select" value="" onChange={(e) => { if (e.target.value) addFinish(e.target.value); }}>
                                <option value="">Finish...</option>
                                {availableFinishes.filter(f => !filters.finishes.includes(f)).map(f => <option key={f} value={f}>{formatFinish(f)}</option>)}
                            </select>
                        )}
                        {effectiveShowSearchButton && <button type="submit" className="filter-search-btn" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>}
                    </div>
                    {extraLeftContent}
                </form>
                {showDisplayOptions && (
                    <>
                        <div className="filter-bar-divider" />
                        <div className="filters-bar-right">
                            <span className="filter-section-label">Sort & Display</span>
                            <select className="filter-select" value={displayOptions.sortBy} onChange={(e) => updateDisplayOptions({ ...displayOptions, sortBy: e.target.value })}>
                            {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <select className="filter-select" value={displayOptions.cardsPerPage} onChange={(e) => { updateDisplayOptions({ ...displayOptions, cardsPerPage: parseInt(e.target.value) }); setCurrentPage(1); }}>
                            <option value={12}>12</option><option value={24}>24</option><option value={48}>48</option><option value={96}>96</option>
                        </select>
                        <select className="filter-select" value={displayOptions.displayMode} onChange={(e) => updateDisplayOptions({ ...displayOptions, displayMode: e.target.value })}>
                            <option value="standard">Standard</option><option value="compact">Compact</option><option value="list">List</option>
                        </select>
                        </div>
                    </>
                )}
            </div>

            {/* Selected filters tags */}
            {(filters.setCodes.length > 0 || filters.rarities.length > 0 || filters.finishes.length > 0) && (
                <div className="selected-filters-bar">
                    {filters.setCodes.map(c => <span key={c} className="filter-tag">{getSetName(c)}<button type="button" className="filter-tag-remove" onClick={() => removeSetCode(c)}>×</button></span>)}
                    {filters.rarities.map(r => <span key={r} className="filter-tag">{r.charAt(0).toUpperCase() + r.slice(1)}<button type="button" className="filter-tag-remove" onClick={() => removeRarity(r)}>×</button></span>)}
                    {filters.finishes.map(f => <span key={f} className="filter-tag filter-tag-finish">{formatFinish(f)}<button type="button" className="filter-tag-remove" onClick={() => removeFinish(f)}>×</button></span>)}
                </div>
            )}

            {/* Card Display Area (widget mode only) */}
            {showCardDisplay && (
                <div className="searchfilterbar-results">
                    {!loading && hasSearched && rawCards.length > 0 && (
                        <div className="results-header">
                            <div className="results-info">
                                Showing <span className="results-count">{Math.min(currentPage * displayOptions.cardsPerPage, sortedCards.length)}</span> of <span className="results-count">{sortedCards.length}</span> cards
                                {filters.finishes.length > 0 && ` (filtered from ${rawCards.length})`}
                            </div>
                        </div>
                    )}
                    {loading && <div className="loading-container"><div className="loading-spinner"></div><div className="loading-text">Searching...</div></div>}
                    {error && !loading && <div className="error-container"><div className="error-title">Error</div><div className="error-message">{error}</div></div>}
                    {!hasSearched && !loading && (
                        <div className="empty-state">
                            <div className="empty-title">{emptyTitle}</div>
                            <div className="empty-message">{emptyMessage}</div>
                            {dualMode && toggleOn && onToggleChange && (
                                <button type="button" className="empty-search-all-btn" onClick={switchToAllCards}>
                                    Search all cards
                                </button>
                            )}
                        </div>
                    )}
                    {hasSearched && !loading && !error && sortedCards.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-title">No cards found</div>
                            <div className="empty-message">{filters.finishes.length > 0 ? 'Try removing the finish filter.' : 'Try different filters.'}</div>
                            {dualMode && toggleOn && onToggleChange && (
                                <button type="button" className="empty-search-all-btn" onClick={switchToAllCards}>
                                    {filters.name.trim()
                                        ? `Search all cards for "${filters.name.trim()}"`
                                        : 'Search all cards'}
                                </button>
                            )}
                        </div>
                    )}
                    {hasSearched && !loading && !error && sortedCards.length > 0 && (
                        <>
                            <div className={`cards-grid ${displayOptions.displayMode === 'compact' ? 'cards-grid-compact' : ''} ${displayOptions.displayMode === 'list' ? 'cards-grid-list' : ''}`}>
                                {paginatedCards.map((card, idx) => {
                                    // Support for collection cards that have {card, binder} structure
                                    const actualCard = card.card || card;
                                    const binderEntry = card.binder || card.entry || null;

                                    // Check if this card is matched (for highlighting)
                                    const keyMatched = matchedCardKeys && matchedCardKeys.size > 0 &&
                                        matchedCardKeys.has(`${actualCard.uuid}-${actualCard.setCode}`);
                                    const nameMatched = matchedCardNames && matchedCardNames.size > 0 && actualCard.name &&
                                        matchedCardNames.has(actualCard.name.trim().toLowerCase());
                                    const isHighlighted = keyMatched || nameMatched;

                                    return (
                                        <UnifiedCard
                                            key={`${actualCard.uuid}-${actualCard.setCode}-${binderEntry?.id || idx}`}
                                            card={actualCard}
                                            entry={binderEntry}
                                            displayMode={displayOptions.displayMode}
                                            setName={getSetName(actualCard.setCode)}
                                            mode={cardMode}
                                            onClick={onCardClick ? () => onCardClick(card) : undefined}
                                            onAddToTrade={onAddToTrade}
                                            onAddToTradeWithFinish={onAddToTradeWithFinish}
                                            onAddToCollection={onAddToCollection}
                                            toggleOn={dualMode ? toggleOn : undefined}
                                            activeTab={activeTab}
                                            isHighlighted={isHighlighted}
                                            entryType={entryType}
                                            onUpdateEntry={onUpdateEntry}
                                            onDeleteEntry={onDeleteEntry}
                                            onInfoClick={() => handleOpenOverlay(idx)}
                                            addDisabled={getAddState ? !!getAddState(actualCard, binderEntry)?.disabled : false}
                                        />
                                    );
                                })}
                            </div>
                            {renderPagination()}
                        </>
                    )}
                </div>
            )}

            {/* Card Info Overlay with Navigation */}
            {showCardDisplay && overlayCard && (
                <CardInfoOverlay
                    show={selectedOverlayIndex !== null}
                    card={overlayCard}
                    entry={overlayEntry}
                    prices={overlayPrices}
                    setDisplayName={getSetName(overlayCard?.setCode)}
                    imageError={overlayImageError}
                    onClose={handleCloseOverlay}
                    onImageError={() => setOverlayImageError(true)}
                    quantityBadge={overlayEntry?.quantity > 1 ? `×${overlayEntry.quantity}` : null}
                    onPrevious={handlePreviousCard}
                    onNext={handleNextCard}
                    hasPrevious={selectedOverlayIndex > 0}
                    hasNext={selectedOverlayIndex < paginatedCards.length - 1}
                />
            )}
        </>
    );
}

export default SearchFilterBar;
